/**
 * LocationProvider — abstract interface
 *
 * Any backend (web Geolocation API, native FusedLocationProvider on Android,
 * Core Location on iOS) implements `subscribe(callback)` returning an
 * unsubscribe function. Each emission is a `LocationSample`:
 *
 *   { lat, lng, accuracy, speedMps?, headingDeg?, tsMs }
 *
 * `headingDeg` here is the GPS-derived course (only valid when moving). The
 * orientation-sensor heading lives in HeadingProvider.
 */

export class LocationProvider {
  // eslint-disable-next-line no-unused-vars
  subscribe(_cb) { throw new Error("LocationProvider.subscribe not implemented"); }
  destroy() {}
  // eslint-disable-next-line class-methods-use-this
  async requestPermission() { return "granted"; }
}

/**
 * Web implementation using navigator.geolocation.watchPosition.
 * High-accuracy mode is required for Uber/Lyft-style fidelity.
 */
export class WebLocationProvider extends LocationProvider {
  constructor({ minIntervalMs = 1000 } = {}) {
    super();
    this.minIntervalMs = minIntervalMs;
    this._watchId = null;
    this._lastEmit = 0;
  }

  async requestPermission() {
    // The geolocation permission flow on the web is implicit: calling
    // watchPosition triggers the browser prompt the first time. We surface
    // the explicit Permissions API state when available so callers can
    // skip the locate-me button being a no-op on denial.
    if (typeof navigator === "undefined" || !navigator.geolocation) return "unavailable";
    if (navigator.permissions?.query) {
      try {
        const status = await navigator.permissions.query({ name: "geolocation" });
        return status.state; // "granted" | "prompt" | "denied"
      } catch { return "prompt"; }
    }
    return "prompt";
  }

  subscribe(cb) {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return () => {};
    }
    this._watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - this._lastEmit < this.minIntervalMs) return;
        this._lastEmit = now;
        const c = pos.coords;
        cb({
          lat: c.latitude,
          lng: c.longitude,
          accuracy: c.accuracy || 30,
          speedMps: typeof c.speed === "number" ? c.speed : null,
          headingDeg: typeof c.heading === "number" && !Number.isNaN(c.heading) ? c.heading : null,
          tsMs: pos.timestamp || now,
        });
      },
      (err) => {
        // Permission denial / position unavailable. Caller handles via the
        // initial requestPermission() check + Permissions API listener.
        // eslint-disable-next-line no-console
        console.warn("[LocationProvider] watchPosition error:", err?.message);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1500,
        timeout: 10000,
      },
    );
    return () => this.destroy();
  }

  destroy() {
    if (this._watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = null;
    }
  }
}
